import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { CircularProgress } from '@material-ui/core';
import { fetchAgentProjectById, ritzDataContext, selectLoggedInAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { fetchHomeKVStore, selectHomeKVStore, userAppDataContext } from '@moderepo/mode-data-state-user-app';
import { BaseInfoCompProps } from '@moderepo/mode-app-components';
import { Agent } from '@moderepo/mode-internal-apis';
import { DataIntervalDashboardWidgetSettings, DashboardWidgetType, HOME_DASHBOARDS_KV_KEY, isDataIntervalDashboardWidgetSettings,
    isHomeDashboardsKeyValuePair, isValueDashboardWidgetSettings, ValueDashboardWidgetSettings, isHeatMapDashboardWidgetSettings,
    HeatMapDashboardWidgetSettings, isProjectDashboardsSettings, WidgetGroupWidgetSettings, isWidgetGroupWidgetSettings } from './dashboardTypes';
import { DataIntervalDashboardWidgetSettingsContainer } from './DataIntervalDashboardWidgetSettingsContainer';
import { ValueDashboardWidgetSettingsContainer } from './ValueDashboardWidgetSettingsContainer';
import { HeatMapDashboardWidgetSettingsContainer } from './HeatMapDashboardWidgetSettingsContainer';
import { WidgetGroupWidgetSettingsContainer } from './WidgetGroupWidgetSettingsContainer';
import { WidgetGroupWidgetViewContainer } from './WidgetGroupWidgetViewContainer';
import { TSDashboardWidgetDataViewContainer } from './TSDashboardWidgetDataViewContainer';
import { LazyComp } from '..';
import { AlertsDashboardWidgetSettings, AlertsDashboardWidgetSettingsContainer, AlertsDashboardWidgetViewContainer,
    EntitiesMapDashboardWidgetSettings, EntitiesMapDashboardWidgetSettingsContainer, EntitiesMapDashboardWidgetViewContainer,
    isAlertsDashboardWidgetSettings, isEntitiesMapDashboardWidgetSettings } from '.';



export interface DashboardWidgetContainerProps {
    readonly className?: string | undefined;
    readonly projectId: number;
    readonly dashboardId: number;

    // Whether this DashboardWidget is in Settings view, the view which we show the widget's settings instead of the widget UI
    readonly isSettingsView?: boolean | undefined;

    // selectedWidget can be either a widget type of a widgetId. If the preview panel is used for creating a new widget
    // then it needs to be a widget type. If the preview panel is used for showing a widget info then it need to be a widgetId
    readonly selectedWidget: DashboardWidgetType | number;

    // The ID of the widget, type WidgetGroup, to place the new widget into. This is OPTIONAL and only for creating new widget.
    readonly widgetGroupId?: number | undefined;

    readonly isInMoveAndResizeMode?: boolean | undefined;

    // The handler for when the widget itself wants to enable move and resize mode. The widget itself doesn't control the mode so it will
    // call the handler to handle the event
    readonly enableMoveAndResizeModeHandler?: ()=> void;


    // Only available if this container is used for creating new widget
    readonly newWidgetHandler?: {
        readonly onNewWidgetCreated: ()=> void;
        readonly onNewWidgetCanceled: ()=> void;
    } | undefined;

    // This is a container that wraps a DUMB component and some time we need to pass some props such as className, styles, etc... into the
    // component from the container of this container so this is a way for us to pass props into the component
    readonly infoCompProps?: {
        readonly className?: BaseInfoCompProps['className'] | undefined;
        readonly formProps?: BaseInfoCompProps['formProps'] | undefined;
    } | undefined;

    /**
     * The context of which page the user is on. Because this component can be shown in different pages, on a Project page where there is no
     * user or home context or on a User's Home page which has user and home context. This data is not required but it helps create a better
     * URL when we take the user to another page.
     */
    readonly routeContext?: {
        readonly userId?: number | undefined;
        readonly homeId?: number | undefined;
    }
}


/**
 * This is a wrapper for the DashboardWidget. It contains all the logic for the widget.
 */
export const DashboardWidgetContainer: React.FC<DashboardWidgetContainerProps> = (props: DashboardWidgetContainerProps) => {

    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;

    // Check the props.selectedWidget to see if it is a number or widget type. If it is a number, we can assume it is a widgetId
    const selectedWidgetId = typeof props.selectedWidget === 'number' ? props.selectedWidget : undefined;
    const isNewWidget = selectedWidgetId === undefined;


    // Select all the dashboards for the Home/Project from the data state
    const dashboards = useMemo(() => {
        if (props.routeContext?.homeId) {
            // Get dashboards settings from Home KV
            const dashboardKV = selectHomeKVStore(appDataState, props.routeContext.homeId, HOME_DASHBOARDS_KV_KEY);
            if (isHomeDashboardsKeyValuePair(dashboardKV)) {
                return dashboardKV.value;
            }
        } else if (project && isProjectDashboardsSettings(project.supportToolSettings.dashboardSettings)) {
            // Get dashboards settings from Project settings
            return project.supportToolSettings.dashboardSettings.dashboards;
        }

        return undefined;
    }, [appDataState, project, props.routeContext]);


    // Select the dashboard object that this Dashboard container is being used for
    const selectedDashboard = useMemo(() => {
        if (dashboards) {
            return dashboards.find((dashboard) => {
                return dashboard.id === props.dashboardId;
            });
        }
        return undefined;
    }, [dashboards, props.dashboardId]);


    // If there is a selectedWidgetId, we need to get the widget settings object for that selectedWidgetId from the home Dashboard data
    const selectedWidget = useMemo(() => {
        if (selectedWidgetId && selectedDashboard) {
            // Then find the widget from the dashboard
            return selectedDashboard.widgets?.find((widget) => {
                return widget.id === selectedWidgetId;
            });
        }

        return undefined;
    }, [selectedDashboard, selectedWidgetId]);


    // Get the list of widgets of type WidgetGroup, we will need to pass these to the widget DataView containers
    const widgetGroups = useMemo((): readonly WidgetGroupWidgetSettings[] => {
        if (selectedDashboard && selectedDashboard.widgets) {
            // Then find the widgets of type WidgetGroup from the dashboard
            return selectedDashboard.widgets.filter((widget) => {
                return isWidgetGroupWidgetSettings(widget);
            }) as readonly WidgetGroupWidgetSettings[];
        }
        return [];
    }, [selectedDashboard]);



    /**
     * Fetch dashboard settings from Home KV or Project Settings
     */
    const fetchDashboardsSettings = useCallback(() => {
        if (props.routeContext?.homeId) {
            // homeId is provided that mean this is Home dashboard, fetch dashboard settings from Home KV
            appDataDispatch(fetchHomeKVStore(props.routeContext.homeId, HOME_DASHBOARDS_KV_KEY), true, true).catch(() => {
                // ignore error because appDataDispatch already handled the error
            });
        } else {
            dataDispatch(fetchAgentProjectById(loggedInAgent.id, props.projectId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [appDataDispatch, dataDispatch, loggedInAgent.id, props.projectId, props.routeContext]);


    /**
     * This useEffect is used for fetching dashboards settings from Home KV or Project Settings. This will only fetch dashboard settings
     * data if we can't find it from cache
     */
    useEffect(() => {
        if (!dashboards) {
            fetchDashboardsSettings();
        }
    }, [dashboards, fetchDashboardsSettings]);


    /**
     * This useEffect is also used for fetching dashboards settings from Home KV or Project Settings. However, this useEffect will always
     * call fetchDashboardsSettings at least once if the component is being used for showing Dashboard Settings. Because we want to make sure
     * the user always see the latest settings before making changes therefore we need to fetch dashboard settings even if we already have
     * it from cache.
     * NOTE: We can't combine this useEffect with the useEffect above and do this
     *      if (!dashboards || props.isSettingsView) {
     *          fetchDashboardsSettings();
     *      }
     * The reason is because if we combine them then the dependencies array will need to include both 'dashboards' and 'props.isSettingsView'.
     * This mean after fetchDashboardsSettings completed, the 'dashboards' state will be modified which will cause the useEffect to be called
     * again and it will call 'fetchDashboardsSettings' again and this will cause an infinite loop.
     */
    useEffect(() => {
        if (props.isSettingsView) {
            fetchDashboardsSettings();
        }
    }, [fetchDashboardsSettings, props.isSettingsView]);


    /**
     * Create a dashboard widget component based on widget type.
     * If props.selectedWidget is a widget type then create a widget component that is used for creating a new widget
     * Else if selectedWidget is not null then create a widget component based on the selectedWidget's type
     */
    const createDashboardWidgetComponent = useCallback(() => {
        if (props.isSettingsView) {
            // Settings View
            if (props.selectedWidget === DashboardWidgetType.DATA_INTERVAL || isDataIntervalDashboardWidgetSettings(selectedWidget)) {
                return (
                    <DataIntervalDashboardWidgetSettingsContainer
                        className={props.className}
                        projectId={props.projectId}
                        dashboardId={props.dashboardId}
                        infoCompProps={props.infoCompProps}
                        newWidgetHandler={props.newWidgetHandler}
                        selectedWidget={selectedWidget as DataIntervalDashboardWidgetSettings}
                        widgetGroupId={props.widgetGroupId}
                        routeContext={props.routeContext}
                    />
                );
            }
            if (props.selectedWidget === DashboardWidgetType.VALUE || isValueDashboardWidgetSettings(selectedWidget)) {
                return (
                    <ValueDashboardWidgetSettingsContainer
                        className={props.className}
                        projectId={props.projectId}
                        dashboardId={props.dashboardId}
                        infoCompProps={props.infoCompProps}
                        newWidgetHandler={props.newWidgetHandler}
                        selectedWidget={selectedWidget as ValueDashboardWidgetSettings}
                        widgetGroupId={props.widgetGroupId}
                        routeContext={props.routeContext}
                    />
                );
            }
            if (props.selectedWidget === DashboardWidgetType.HEAT_MAP || isHeatMapDashboardWidgetSettings(selectedWidget)) {
                return (
                    <HeatMapDashboardWidgetSettingsContainer
                        className={props.className}
                        projectId={props.projectId}
                        dashboardId={props.dashboardId}
                        infoCompProps={props.infoCompProps}
                        newWidgetHandler={props.newWidgetHandler}
                        selectedWidget={selectedWidget as HeatMapDashboardWidgetSettings}
                        widgetGroupId={props.widgetGroupId}
                        routeContext={props.routeContext}
                    />
                );
            }
            if (props.selectedWidget === DashboardWidgetType.ALERTS || isAlertsDashboardWidgetSettings(selectedWidget)) {
                return (
                    <AlertsDashboardWidgetSettingsContainer
                        className={props.className}
                        projectId={props.projectId}
                        dashboardId={props.dashboardId}
                        infoCompProps={props.infoCompProps}
                        newWidgetHandler={props.newWidgetHandler}
                        selectedWidget={selectedWidget as AlertsDashboardWidgetSettings}
                        widgetGroupId={props.widgetGroupId}
                        routeContext={props.routeContext}
                    />
                );
            }
            if (props.selectedWidget === DashboardWidgetType.ENTITIES_MAP || isEntitiesMapDashboardWidgetSettings(selectedWidget)) {
                return (
                    <EntitiesMapDashboardWidgetSettingsContainer
                        className={props.className}
                        projectId={props.projectId}
                        dashboardId={props.dashboardId}
                        infoCompProps={props.infoCompProps}
                        newWidgetHandler={props.newWidgetHandler}
                        selectedWidget={selectedWidget as EntitiesMapDashboardWidgetSettings}
                        widgetGroupId={props.widgetGroupId}
                        routeContext={props.routeContext}
                    />
                );
            }
            if (props.selectedWidget === DashboardWidgetType.WIDGET_GROUP || isWidgetGroupWidgetSettings(selectedWidget)) {
                return (
                    <WidgetGroupWidgetSettingsContainer
                        className={props.className}
                        projectId={props.projectId}
                        dashboardId={props.dashboardId}
                        infoCompProps={props.infoCompProps}
                        newWidgetHandler={props.newWidgetHandler}
                        selectedWidget={selectedWidget as WidgetGroupWidgetSettings}
                        routeContext={props.routeContext}
                    />
                );
            }
        } else {
            // Data View. For data view, only WidgetGroup has a different component. All other widget types can use
            // TSDBDashboardWidgetDataViewContainer
            if (isWidgetGroupWidgetSettings(selectedWidget)) {
                return (
                    <WidgetGroupWidgetViewContainer
                        projectId={props.projectId}
                        dashboardId={props.dashboardId}
                        selectedWidget={selectedWidget}
                        isInMoveAndResizeMode={props.isInMoveAndResizeMode}
                        enableMoveAndResizeModeHandler={props.enableMoveAndResizeModeHandler}
                        routeContext={props.routeContext}
                    />
                );
            }
            if (isAlertsDashboardWidgetSettings(selectedWidget)) {
                return (
                    <AlertsDashboardWidgetViewContainer
                        projectId={props.projectId}
                        dashboardId={props.dashboardId}
                        infoCompProps={props.infoCompProps}
                        selectedWidget={selectedWidget}
                        widgetGroups={widgetGroups}
                        isInMoveAndResizeMode={props.isInMoveAndResizeMode}
                        enableMoveAndResizeModeHandler={props.enableMoveAndResizeModeHandler}
                        routeContext={props.routeContext}
                    />
                );
            }
            if (isEntitiesMapDashboardWidgetSettings(selectedWidget)) {
                return (
                    <EntitiesMapDashboardWidgetViewContainer
                        projectId={props.projectId}
                        dashboardId={props.dashboardId}
                        infoCompProps={props.infoCompProps}
                        selectedWidget={selectedWidget}
                        widgetGroups={widgetGroups}
                        isInMoveAndResizeMode={props.isInMoveAndResizeMode}
                        enableMoveAndResizeModeHandler={props.enableMoveAndResizeModeHandler}
                        routeContext={props.routeContext}
                    />
                );
            }
            if (
                isDataIntervalDashboardWidgetSettings(selectedWidget)
                || isValueDashboardWidgetSettings(selectedWidget)
                || isHeatMapDashboardWidgetSettings(selectedWidget)
            ) {
                return (
                    <TSDashboardWidgetDataViewContainer
                        projectId={props.projectId}
                        dashboardId={props.dashboardId}
                        infoCompProps={props.infoCompProps}
                        selectedWidget={selectedWidget}
                        widgetGroups={widgetGroups}
                        isInMoveAndResizeMode={props.isInMoveAndResizeMode}
                        enableMoveAndResizeModeHandler={props.enableMoveAndResizeModeHandler}
                        routeContext={props.routeContext}
                    />
                );
            }
        }


        if (typeof selectedWidget === 'object') {
            // eslint-disable-next-line no-console
            console.log('Invalid widget config', selectedWidget);
        } else {
            // eslint-disable-next-line no-console
            console.log('Invalid widget type', props.selectedWidget);
        }

        return <></>;
    }, [props.selectedWidget, props.isSettingsView, props.projectId, props.dashboardId, props.infoCompProps, props.isInMoveAndResizeMode,
        props.routeContext, props.className, props.newWidgetHandler, props.widgetGroupId, props.enableMoveAndResizeModeHandler, selectedWidget,
        widgetGroups]);


    return (
        <>
            {project && (selectedWidget || isNewWidget) && (
                <LazyComp disabled={props.isSettingsView === true}>
                    {createDashboardWidgetComponent()}
                </LazyComp>
            )}

            {(!project || (!isNewWidget && !selectedWidget)) && (
                <CircularProgress />
            )}
        </>
    );
};
